<template>
  <PathwayInlineSurveyForm
    ref="form"
    :pathway="pathway"
    :step="step"
    :consumer-key="consumerKey"
    :survey="survey"
    :prefill="true"
    :submit-disabled="pets.length ? !selectedPet : !newPetCategoryId"
    :after-answers="storeLifestyle"
  >
    <template #before-form>
      <PetPicker
        v-if="pets.length"
        v-model="selectedPet"
        :pets="pets"
        :qualified-pet-ids="qualifiedPetIds"
        class="mb-4"
      />
      <div v-else class="mb-4 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <p class="text-sm text-gray-600">
          Ce patient n'a pas encore d'animal enregistré : renseignez-le, il sera créé avec ses réponses.
        </p>
        <PetIdentityFields v-model:name="newPetName" v-model:category-id="newPetCategoryId" />
      </div>
    </template>
    <template #verdict-actions="{loading}">
      <BaseButton v-if="remainingPets.length" :loading="loading" @click="qualifyAnotherPet">
        Répondre pour un autre animal
      </BaseButton>
    </template>
  </PathwayInlineSurveyForm>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref, useTemplateRef} from "vue";
import {storeToRefs} from "pinia";
import {Pathway, PathwayStep} from "@/program/interfaces/Program";
import {Survey, SurveyQuestion} from "@/core/interfaces/survey/Survey";
import {Pet, PetDeclaredRecency, PetHabitat, PetLifestylePayload} from "@/customer/interfaces/Pet";
import {SurveyAnswerValues} from "@/pathway/interfaces/PathwaySurvey";
import PathwayInlineSurveyForm from "@/pathway/components/PathwayInlineSurveyForm.vue";
import PetPicker from "@/program/components/support-program-addons/pets/PetPicker.vue";
import PetIdentityFields from "@/customer/components/customer-beneficiary/PetIdentityFields.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {petApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import {usePatientStore} from "@/stores/patient.store";

/**
 * Answer slugs of the qualification questions, in the order they are seeded.
 * Only used as a fallback for an API not yet exposing the answer slugs.
 */
const RECENCY_SLUGS: PetDeclaredRecency[] = [
    'unknown',
    'less_than_3_months',
    'between_3_and_6_months',
    'more_than_6_months'
];
const HABITAT_SLUGS: PetHabitat[] = ['indoor', 'outdoor'];

defineProps<{
    pathway: Pathway;
    step: PathwayStep;
    consumerKey: string;
    survey: Survey;
}>();

const {showError} = useToastStore();
const patientStore = usePatientStore();
const {customer} = storeToRefs(patientStore);

const form = useTemplateRef<InstanceType<typeof PathwayInlineSurveyForm>>('form');
const selectedPet = ref<Pet | null>(null);
const qualifiedPetIds = ref<number[]>([]);
const newPetName = ref<string>();
const newPetCategoryId = ref<string>();

/**
 * The pets of the patient, the only ones this step can qualify.
 */
const pets = computed<Pet[]>(() => customer.value?.pets ?? []);

/**
 * The pets not yet qualified during this step.
 */
const remainingPets = computed<Pet[]>(() => {
    return pets.value.filter(pet => !qualifiedPetIds.value.includes(pet.id));
});

/**
 * A single pet needs no choice from the pharmacist.
 */
onMounted(() => {
    selectedPet.value = pets.value.length === 1 ? pets.value[0] : null;
});

/**
 * Create the pet described in the form when the patient has none yet, and
 * select it so the answers are stored on it.
 */
const createPet = async (): Promise<Pet | null> => {
    if (!newPetCategoryId.value || !customer.value) {
        return null;
    }

    return petApi().create({
        name: newPetName.value,
        category_id: Number(newPetCategoryId.value),
        customer_id: customer.value.id
    })
        .then(async pet => {
            await patientStore.updatePet(pet);
            selectedPet.value = pet;

            return pet;
        })
        .catch(() => {
            showError({
                content: "L'animal n'a pas pu être créé."
            });

            return null;
        });
};

/**
 * Store the profile declared for the selected pet, created first when the
 * patient had none.
 *
 * The survey answers are already saved at this point, so a failure here is
 * only reported and must not interrupt the questionnaire.
 */
const storeLifestyle = async (survey: Survey, answers: SurveyAnswerValues) => {
    const pet = selectedPet.value ?? await createPet();

    if (!pet) {
        return;
    }

    const payload = lifestylePayload(survey, answers);

    if (!Object.keys(payload).length) {
        return;
    }

    await petApi().updateLifestyle(pet.id, payload)
        .then(async lifestyle => {
            qualifiedPetIds.value = [...qualifiedPetIds.value, pet.id];
            await patientStore.updatePet({...pet, lifestyle});
        })
        .catch(() => {
            showError({
                content: `Le profil de ${pet.name} n'a pas pu être enregistré.`
            });
        });
};

/**
 * Map the submitted answers to the pet lifestyle attributes, dropping the
 * questions left unanswered.
 */
const lifestylePayload = (survey: Survey, answers: SurveyAnswerValues): PetLifestylePayload => {
    const [deworming, antiparasitic, habitat, risk] = (survey.sections ?? [])
        .flatMap(section => section.questions ?? []);

    const attributes = {
        declared_last_deworming: answerSlug(deworming, RECENCY_SLUGS, answers),
        declared_last_external_antiparasitic: answerSlug(antiparasitic, RECENCY_SLUGS, answers),
        habitat: answerSlug(habitat, HABITAT_SLUGS, answers),
        exposed_to_risk: answerBoolean(risk, answers)
    };

    return Object.fromEntries(
        Object.entries(attributes).filter(([, value]) => value !== null)
    ) as PetLifestylePayload;
};

/**
 * The slug of the answer selected for a choice question.
 *
 * @param question The question to read the answer of.
 * @param slugs The answer slugs, in the order the survey is seeded.
 * @param answers The submitted answers.
 */
const answerSlug = <T extends string>(
    question: SurveyQuestion | undefined,
    slugs: T[],
    answers: SurveyAnswerValues
): T | null => {
    if (!question) {
        return null;
    }

    const value = answers[question.id]?.[0];
    const choices = (question.answers ?? []) as { value: unknown, slug?: string }[];
    const index = choices.findIndex(choice => String(choice.value) === String(value));

    if (index === -1) {
        return null;
    }

    return (choices[index].slug as T) ?? slugs[index] ?? null;
};

/**
 * The boolean answer given to a yes/no question.
 */
const answerBoolean = (question: SurveyQuestion | undefined, answers: SurveyAnswerValues): boolean | null => {
    const value = question ? answers[question.id]?.[0] : undefined;

    return typeof value === 'boolean' ? value : null;
};

/**
 * Restart the questionnaire for the next pet left to qualify.
 */
const qualifyAnotherPet = () => {
    form.value?.reset();
    selectedPet.value = remainingPets.value[0] ?? null;
};

</script>
